import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2, Pencil, Plus, Settings2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { commsApi } from './phase567Api';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';

// Ticket 195: a small library of pre-approved message templates. Every staff
// seat picks from it in the composer; only "Manage staff" holders (backend
// ACTION_KEYS["templates.manage"] -> staff.manage) write it.
export function canManageTemplates(user) {
  return !!(user?.is_super_admin || (user?.permissions || []).includes('staff.manage'));
}

const CHANNEL_LABELS = { sms: 'Text', email: 'Email' };

// Composer button: loads the templates for the active channel when opened
// (never on mount, so a thread doesn't pay for it), and hands the picked one
// to `onPick`.
export function TemplatePicker({ channel, onPick, canManage = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    setItems(null);
    try {
      const r = await commsApi.listTemplates({ channel });
      setItems(r?.items || []);
    } catch {
      setError(true);
    }
  }, [channel]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const pick = (t) => {
    onPick(t);
    setOpen(false);
  };

  return (
    <div className="relative" data-testid="template-picker">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 px-1.5 py-0.5 rounded hover:bg-slate-100"
        data-testid="template-picker-btn">
        <FileText size={12} /> Templates
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1 z-20 w-72 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg p-1"
          role="listbox" aria-label={`${CHANNEL_LABELS[channel] || ''} templates`} data-testid="template-picker-list">
          {error ? (
            <div className="p-2 text-xs text-red-600">
              Couldn't load templates.{' '}
              <button type="button" className="underline" onClick={load}>Retry</button>
            </div>
          ) : items === null ? (
            <div className="p-2 text-xs text-slate-500 flex items-center gap-1"><Loader2 className="animate-spin" size={12} /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-2 text-xs text-slate-500" data-testid="template-picker-empty">
              No {CHANNEL_LABELS[channel]?.toLowerCase() || ''} templates yet.
              {!canManage && ' Ask an admin to add some.'}
            </div>
          ) : (
            items.map((t) => (
              <button key={t.template_id} type="button" role="option" aria-selected={false} onClick={() => pick(t)}
                className="w-full text-left rounded px-2 py-1.5 hover:bg-slate-100"
                data-testid={`template-option-${t.template_id}`}>
                <div className="text-xs font-medium text-slate-800 truncate">{t.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{t.body}</div>
              </button>
            ))
          )}
          {canManage && (
            <button type="button" onClick={() => { setOpen(false); setManageOpen(true); }}
              className="w-full mt-1 border-t border-slate-100 text-left px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1"
              data-testid="template-manage-btn">
              <Settings2 size={12} /> Manage templates
            </button>
          )}
        </div>
      )}
      {canManage && <TemplatesManagerDialog open={manageOpen} onOpenChange={setManageOpen} />}
    </div>
  );
}

const EMPTY_FORM = { name: '', channel: 'sms', subject: '', body: '' };

// Create / edit / delete the tenant's templates. Rendered only for managers;
// the backend refuses everyone else regardless.
export function TemplatesManagerDialog({ open, onOpenChange }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [form, setForm] = useState(null); // null = list view; {template_id?} = editing
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await commsApi.listTemplates();
      setItems(r?.items || []);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm(null);
    setConfirmDelete(null);
    setItems(null);
    load();
  }, [open, load]);

  const ready = form && form.name.trim() && form.body.trim();

  const save = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const subject = form.channel === 'email' ? form.subject.trim() : '';
      if (form.template_id) {
        await commsApi.updateTemplate(form.template_id, {
          name: form.name.trim(), body: form.body, subject: subject || null,
        });
      } else {
        await commsApi.createTemplate({
          name: form.name.trim(), channel: form.channel, body: form.body,
          ...(subject ? { subject } : {}),
        });
      }
      setForm(null);
      await load();
    } catch (e) {
      toast({ title: "Couldn't save the template", description: errorMessage(e), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (confirmDelete !== t.template_id) { setConfirmDelete(t.template_id); return; }
    try {
      await commsApi.deleteTemplate(t.template_id);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast({ title: "Couldn't delete the template", description: errorMessage(e), variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="templates-dialog">
        <DialogHeader>
          <DialogTitle>Message templates</DialogTitle>
          <DialogDescription>The approved texts and emails your team can start from in the Inbox.</DialogDescription>
        </DialogHeader>
        {form ? (
          <div className="space-y-3" data-testid="template-form">
            {!form.template_id && (
              <div className="flex gap-1" role="group" aria-label="Channel">
                {['sms', 'email'].map((ch) => (
                  <button key={ch} type="button" onClick={() => setForm({ ...form, channel: ch })}
                    aria-pressed={form.channel === ch}
                    className={`px-3 py-1.5 rounded-full text-sm border ${form.channel === ch ? 'border-[#070B5F] bg-[#070B5F]/5' : 'border-slate-200'}`}
                    data-testid={`template-channel-${ch}`}>
                    {CHANNEL_LABELS[ch]}
                  </button>
                ))}
              </div>
            )}
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Name" maxLength={80} aria-label="Template name" data-testid="template-name" />
            {form.channel === 'email' && (
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Subject (optional)" maxLength={200} aria-label="Subject" data-testid="template-subject" />
            )}
            <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Message" rows={5} maxLength={5000} aria-label="Message" data-testid="template-body" />
            <div className="flex justify-end gap-2">
              <button type="button" className="text-sm px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50" onClick={() => setForm(null)}>Cancel</button>
              <button type="button" className="btn-gold text-sm !px-4 !py-1.5" disabled={!ready || saving}
                onClick={save} data-testid="template-save">
                {saving && <Loader2 className="animate-spin" size={14} />} Save
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {error ? (
              <p className="text-sm text-red-600">
                Couldn't load templates. <button type="button" className="underline" onClick={load}>Retry</button>
              </p>
            ) : items === null ? (
              <p className="text-sm text-slate-500 flex items-center gap-1"><Loader2 className="animate-spin" size={14} /> Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="templates-empty">No templates yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto" data-testid="templates-list">
                {items.map((t) => (
                  <li key={t.template_id} className="py-2 flex items-start gap-2" data-testid={`template-row-${t.template_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 truncate">
                        {t.name} <span className="text-[10px] font-normal text-slate-500 ml-1">{CHANNEL_LABELS[t.channel] || t.channel}</span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">{t.body}</div>
                    </div>
                    <button type="button" aria-label={`Edit ${t.name}`} className="p-1 text-slate-500 hover:text-slate-800"
                      onClick={() => setForm({ template_id: t.template_id, name: t.name, channel: t.channel, subject: t.subject || '', body: t.body })}
                      data-testid={`template-edit-${t.template_id}`}>
                      <Pencil size={14} />
                    </button>
                    <button type="button" aria-label={`Delete ${t.name}`}
                      className={`p-1 text-xs ${confirmDelete === t.template_id ? 'text-red-700 font-medium' : 'text-slate-500 hover:text-red-700'}`}
                      onClick={() => remove(t)} data-testid={`template-delete-${t.template_id}`}>
                      {confirmDelete === t.template_id ? 'Confirm delete' : <Trash2 size={14} />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button type="button" onClick={() => setForm({ ...EMPTY_FORM })}
              className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900"
              data-testid="template-new">
              <Plus size={14} /> New template
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
